import { AccredibleResponsiveBreakpoint } from '@accredible-frontend-v2/utils/responsive-helper';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  Directive,
  ElementRef,
  HostListener,
  Input,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatLegacyMenu as MatMenu } from '@angular/material/legacy-menu';
import Bugsnag from '@bugsnag/js';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { delay, skip, tap } from 'rxjs/operators';
import { AccredibleResponsiveMenuItem } from '../../models/responsive-menu.model';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[dynamicMenuItems]',
})
export class DynamicMenuItemsDirective {}

@UntilDestroy()
@Component({
  selector: 'accredible-responsive-menu',
  templateUrl: './responsive-menu.component.html',
  styleUrls: [`./responsive-menu.component.scss`],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResponsiveMenuComponent implements AfterViewInit, AfterViewChecked {
  @Input()
  menuTriggerButtonId: string;
  @Input()
  dynamicMenuItems: AccredibleResponsiveMenuItem[] = [];
  @Input()
  staticMenuItems: AccredibleResponsiveMenuItem[] = [];
  @Input()
  maxItemHeight = 0;
  @Input()
  redrawObservable: Observable<never>;

  @ViewChild('menuContainer')
  menuContainer: ElementRef<HTMLElement>;
  @ViewChild('triggerButtonContainer')
  triggerButtonContainer: ElementRef<HTMLElement>;
  @ViewChild('menu')
  matMenu: MatMenu;

  @ContentChild(DynamicMenuItemsDirective, { read: TemplateRef })
  dynamicMenuItemsTemplateRef: TemplateRef<any>;
  @ContentChild(TemplateRef)
  menuTriggerButtonTemplateRef: TemplateRef<any>;

  dynamicMenuItemsInPopupMenu: AccredibleResponsiveMenuItem[] = [];
  initialHeight: number;

  private _resizeTimeout: number;
  private _isInitDone = false;
  private _maxItemHeight: number;
  private _triggerButtonContainerWidth = 0;

  constructor(private readonly _el: ElementRef, private readonly _cdr: ChangeDetectorRef) {}

  @HostListener('window:resize')
  onResize(): void {
    // This logic hides and shows the triggerButtonContainer children with the class 'trigger-hide'
    if (window.innerWidth < AccredibleResponsiveBreakpoint.XS) {
      this._setTriggerButtonElementsDisplay('none');
    } else {
      this._setTriggerButtonElementsDisplay('');
    }

    // Window decreasing size
    const currentHeight = this.menuContainer.nativeElement.offsetHeight;
    if (currentHeight > this.initialHeight) {
      this._addToPopupMenu();
      return;
    }

    // Window increasing size
    const currentWidth = this.menuContainer.nativeElement.offsetWidth;
    const currentElementsWidthSum = this.dynamicMenuItems.reduce((acc, cur) => {
      return acc + cur.elementWidth;
    }, 0);

    if (
      this.dynamicMenuItemsInPopupMenu.length > 0 &&
      currentWidth >
        currentElementsWidthSum +
          this.dynamicMenuItemsInPopupMenu[0].elementWidth +
          this._triggerButtonContainerWidth +
          20 // To avoid a menu item being added back to the page when it doesn't quite fit
    ) {
      this._removeFromPopupMenu();
    }
  }

  ngAfterViewInit(): void {
    this._setRedrawSubscription();
    // If we have dynamic items, visibility will be set in the _addInitialPopupMenuElements method triggered by the _init method
    this._el.nativeElement.style.visibility =
      this.dynamicMenuItems.length > 0 ? 'hidden' : 'visible';
  }

  /**
   * ngAfterViewChecked is used because some containers might only populate dynamicMenuItems
   * after an API call, so we want to make sure that the dynamicMenuItems are correctly initiated.
   * Since AfterViewChecked runs everytime something changes we also want to make sure that init()
   * only runs once.
   *
   * NOTE: IE is not supported
   */
  ngAfterViewChecked(): void {
    if (!this._isInitDone && this.dynamicMenuItems.length > 0) {
      this._maxItemHeight = this.maxItemHeight;
      this._init();
    }
  }

  private _init(): void {
    // Get the DOM rendered elements of the dynamicMenuItems
    const dynamicMenuItemElements = this.menuContainer.nativeElement.children;

    if (dynamicMenuItemElements.length <= this.dynamicMenuItems.length) {
      Bugsnag.notify(
        `Responsive menu render issue. dynamicMenuItemElements = ${dynamicMenuItemElements.length}; this.dynamicMenuItems = ${this.dynamicMenuItems.length}`,
      );
    }

    for (let i = 0; i < this.dynamicMenuItems.length; i++) {
      const child = dynamicMenuItemElements[i];
      const childStyle = getComputedStyle(child);
      const childHeight =
        dynamicMenuItemElements[i].clientHeight +
        parseFloat(childStyle.borderTopWidth) +
        parseFloat(childStyle.borderBottomWidth) +
        parseFloat(childStyle.marginTop) +
        parseFloat(childStyle.marginBottom);

      if (childHeight > this._maxItemHeight) {
        this._maxItemHeight = childHeight;
      }

      // Save menu element width including its margins
      this.dynamicMenuItems[i].elementWidth =
        dynamicMenuItemElements[i].clientWidth +
        parseFloat(childStyle.borderLeftWidth) +
        parseFloat(childStyle.borderRightWidth) +
        parseFloat(childStyle.marginLeft) +
        parseFloat(childStyle.marginRight);
      this.dynamicMenuItems[i].htmlElement = dynamicMenuItemElements[i];
    }

    this._addInitialPopupMenuElements();
    this._isInitDone = true;
  }

  /**
   * Check if we need to add any menu element to the popup menu.
   * This is a recursive method.
   */
  private _addInitialPopupMenuElements(): void {
    if (this.menuContainer.nativeElement.offsetHeight > this._maxItemHeight) {
      this._addToPopupMenu();

      // Make next line async in order to not block DOM rendering, this way the offsetHeight will be updated correctly
      setTimeout(() => {
        this._addInitialPopupMenuElements();
      }, 0);
    } else {
      // After checking if there were any menu elements to be removed,
      // save the width of the trigger button container
      this._setTriggerButtonContainerWidth();
      // Set initial height and trigger resize manually after angular has added the trigger button to the DOM
      setTimeout(() => {
        const triggerButtonContainerHeight = this.triggerButtonContainer.nativeElement.offsetHeight;
        this.initialHeight = Math.max(this._maxItemHeight, triggerButtonContainerHeight);
        this.onResize();
        // Tell angular that the menu items arrays may have changed
        this._cdr.markForCheck();

        // In order to prevent menu item wrapping issues on small mobile displays we trigger the onResize method one final time to adjust menu content
        if (this._resizeTimeout) {
          clearTimeout(this._resizeTimeout);
        }

        this._resizeTimeout = setTimeout(() => {
          this.onResize();
          // Visibility is initially set to hidden in order to not see the second row of menu items disappear when loading the page,
          // and to stop flickering we only set the menu to visible when all logic has run
          this._el.nativeElement.style.visibility = 'visible';
        }, 0);
      }, 250);
    }
  }

  private _addToPopupMenu(): void {
    const elToAdd = this.dynamicMenuItems[this.dynamicMenuItems.length - 1];
    if (elToAdd) {
      this.dynamicMenuItemsInPopupMenu = [elToAdd, ...this.dynamicMenuItemsInPopupMenu];
      this.dynamicMenuItems = this.dynamicMenuItems.slice(0, this.dynamicMenuItems.length - 1);
      elToAdd.htmlElement?.remove();
      this._setTriggerButtonContainerWidth();
    }
  }

  private _removeFromPopupMenu(): void {
    const elToRemove = this.dynamicMenuItemsInPopupMenu[0];
    if (elToRemove) {
      this.dynamicMenuItems = [...this.dynamicMenuItems, elToRemove];
      this.dynamicMenuItemsInPopupMenu = this.dynamicMenuItemsInPopupMenu.slice(1);
      this.menuContainer.nativeElement.insertBefore(
        elToRemove.htmlElement,
        this.triggerButtonContainer.nativeElement,
      );
      this._setTriggerButtonContainerWidth();
    }
  }

  private _setTriggerButtonContainerWidth(): void {
    // Save the width of the trigger button container including its margins
    const triggerButtonContainer = this.triggerButtonContainer.nativeElement;
    const tbcStyle = getComputedStyle(triggerButtonContainer);
    this._triggerButtonContainerWidth =
      triggerButtonContainer.clientWidth +
      parseFloat(tbcStyle.borderLeftWidth) +
      parseFloat(tbcStyle.borderRightWidth) +
      parseFloat(tbcStyle.marginLeft) +
      parseFloat(tbcStyle.marginRight);
  }

  private _setTriggerButtonElementsDisplay(display: string): void {
    const triggerElementsToHide = Array.from(
      this.triggerButtonContainer.nativeElement.getElementsByClassName('trigger-hide'),
    );
    if (triggerElementsToHide.length > 0) {
      triggerElementsToHide.forEach((el: HTMLElement) => {
        el.style.display = display;
      });
      this._setTriggerButtonContainerWidth();
    }
  }

  /**
   * If redrawObservable is passed in, everytime a subscription is triggered,
   * redraw the component.
   * An example is when changing language, this component should be redrawn,
   * because maybe with new language more or less items will fit in on the screen
   */
  private _setRedrawSubscription(): void {
    if (this.redrawObservable) {
      this.redrawObservable
        .pipe(
          untilDestroyed(this),
          skip(1),
          tap(() => {
            while (this.dynamicMenuItemsInPopupMenu.length > 0) {
              this._removeFromPopupMenu();
            }
          }),
          // delay(0) to be async and give time for the browser to rerender
          delay(0),
        )
        .subscribe(() => {
          this._init();
        });
    }
  }
}
